import {
  ExploreSectionItem,
  ExploreSectionItemType,
  isExploreSectionHashtagHighlightItem,
  isExploreSectionHashtagItem,
  SourceType,
} from 'types';
import { METATOPICS } from 'utils/metatopics.constants';
import {
  DAILY_PICKS_ID,
  HASHTAG_ID_TYPE_PREFIX,
  HIGHLIGHTS_ID_PREFIX,
  SOURCES_TYPE_ID_TYPE_PREFIX,
} from 'utils/explore.constants';

//TODO: Move to constants
export const DAILY_PICKS_ID_PREFIX = 'explore-daily-';

export const getExploreSectionItemIllustration = ({
  id,
  type,
}: {
  type: ExploreSectionItemType;
  id: string;
}): string => {
  switch (type) {
    case 'highlight-hashtag': {
      // Remove the highlight prefix to match the hashtag id
      const hashtagId = id.substr(HIGHLIGHTS_ID_PREFIX.length);
      return (
        METATOPICS.find(topic => topic.exploreId === hashtagId)?.image ?? ''
      );
    }
    case 'hashtag': {
      return METATOPICS.find(topic => topic.exploreId === id)?.image ?? '';
    }
    case 'source_type': {
      const sourceType = id.substr(SOURCES_TYPE_ID_TYPE_PREFIX.length);
      return `https://static.deepstash.com/illustrations/categories/${sourceType}.svg`;
    }
    case 'daily-pick': {
      return 'https://static.deepstash.com/illustrations/categories/daily_picks.svg';
    }
    default: {
      return '';
    }
  }
};

/**
 * Get the type of an explore section item based on its id
 */
export const getExploreSectionItemType = (
  id: string,
): ExploreSectionItemType => {
  if (id === DAILY_PICKS_ID) {
    return 'daily-pick';
  } else if (id.includes(HIGHLIGHTS_ID_PREFIX)) {
    if (id.includes(HASHTAG_ID_TYPE_PREFIX)) {
      return 'highlight-hashtag';
    }
  } else if (id.includes(HASHTAG_ID_TYPE_PREFIX)) {
    return 'hashtag';
  } else if (id.includes(SOURCES_TYPE_ID_TYPE_PREFIX)) {
    return 'source_type';
  }
  return 'not-implemented';
};

export const getHashtagNameFromSectionItemId = (id: string) => {
  if (id.includes(HIGHLIGHTS_ID_PREFIX)) {
    return id.substring(
      HASHTAG_ID_TYPE_PREFIX.length + HIGHLIGHTS_ID_PREFIX.length,
    );
  } else {
    return id.substring(HASHTAG_ID_TYPE_PREFIX.length);
  }
};

export const getInitialSectionFromExploreDestinationPath = (
  destinationPath: string,
) => {
  // Get the query params from the string
  const queryParams = (destinationPath.split('?')[1] ?? '')
    .split('&')
    .reduce((acc, keyValueString) => {
      const [key, value] = keyValueString.split('=');
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>);

  return queryParams['initial_section'];
};

export const getExploreSectionItemLink = (
  exploreSectionItem: ExploreSectionItem,
) => {
  switch (exploreSectionItem.type) {
    case 'daily-pick': {
      return '/explore/daily-picks';
    }
    case 'hashtag': {
      if (isExploreSectionHashtagItem(exploreSectionItem)) {
        return `/topic/${exploreSectionItem.hashtagName}`;
      } else {
        // Can't navigate to the topic page
        return undefined;
      }
    }
    case 'highlight-hashtag': {
      if (isExploreSectionHashtagHighlightItem(exploreSectionItem)) {
        return `/topic/${exploreSectionItem.hashtagName}/?initial_section=${exploreSectionItem.initialSection}`;
      } else {
        // Can't navigate to the topic page
        return undefined;
      }
    }
    case 'source_type': {
      if (exploreSectionItem.id === 'sources-by-type-collections') {
        return '/all-collections';
      }
      return `/explore/sources/page/${exploreSectionItem.id.substring(
        SOURCES_TYPE_ID_TYPE_PREFIX.length,
      )}`;
    }
    default: {
      return undefined;
    }
  }
};

export const shouldIgnoreExploreItem = (item: ExploreSectionItem) => {
  // We ignore highlight cards
  if (item.type === 'highlight-hashtag') {
    return true;
  }
  return false;
};

export const getSourceTypeIllustration = (sourceType: SourceType | 6) => {
  switch (sourceType) {
    case SourceType.ARTICLE: {
      return `https://static.deepstash.com/illustrations/categories/articles.svg`;
    }
    case SourceType.BOOK: {
      return `https://static.deepstash.com/illustrations/categories/books.svg`;
    }
    case SourceType.PODCAST: {
      return `https://static.deepstash.com/illustrations/categories/podcasts.svg`;
    }
    case SourceType.VIDEO: {
      return `https://static.deepstash.com/illustrations/categories/videos.svg`;
    }
    case 6:
    default: {
      return 'https://static.deepstash.com/illustrations/categories/collections.svg';
    }
  }
};

// All the url params that can be found for the explore source type page and see all

export type ExplorePageSourceType =
  | 'articles'
  | 'books'
  | 'videos'
  | 'podcasts';
export type ExplorePageType =
  | 'read_next'
  | 'top_stashers'
  | 'popular'
  | 'page'
  | 'newest';

export function isExplorePageSourceType(
  param: string,
): param is ExplorePageSourceType {
  return (
    param === 'articles' ||
    param === 'books' ||
    param === 'videos' ||
    param === 'podcasts'
  );
}

export function isExplorePageType(param: string): param is ExplorePageType {
  return (
    param === 'read_next' ||
    param === 'top_stashers' ||
    param === 'popular' ||
    param === 'newest' ||
    param === 'page'
  );
}
